import { Component, Vue, Prop } from 'vue-property-decorator';
import WithRender from './UpdateRequestDetails.html';
import { Inject } from 'vue-di-container';
import { Toaster } from '../../../../Services/toast';
import UserService from '../../../../shared/userService/UserService';
import UserRoleService from '../../../../shared/userService/UserRoleService';
import UpdateRequestsService from '../UpdateRequestsService';

@WithRender
@Component({
    components: {
    },
    diProvide: [
        UpdateRequestsService,
        UserService,
        UserRoleService,
    ],
})
export default class UpdateRequestDetails extends Vue {
    @Inject(UpdateRequestsService) public service!: UpdateRequestsService;
    @Inject(UserService) public userService!: UserService;
    @Inject(UserRoleService) public userRoleService!: UserRoleService;

    @Prop({ type: Boolean, default: false })
    public layoutSup: boolean | any;

    public userRole: number = 0;
    public User: any = null;
    public rejectDialog: boolean = false;
    public rejectReason: string = '';
    public approveDialog: boolean = false;
    public details: any = {
        id: '',
        displayId: '',
        clientName: '',
        senderName: '',
        senderType: 0,
        customerType: 0,
        crNumber: '',
        enterpriseUnifiedNumber: '',
        senderExpiryDate: '',
        requestStatus: '',
        requestStatusId: 0,
        createdDate: '',
        providerComment: '',
        adminComment: '',
        rejectionReason: '',
        updateTypeName: '',
        currentRepresentativeName: '',
        currentRepresentativeEmail: '',
        currentRepresentativeMobile: '',
        requestedRepresentativeName: '',
        requestedRepresentativeEmail: '',
        requestedRepresentativeMobile: '',
        delegationLetterChanged: false,
        currentDelegationLetterFileName: '',
        requestedDelegationLetterFileName: '',
        changedFields: [],
    };

    public async mounted() {
        this.$emit('childinit', this.layoutSup);
        this.User = await this.userService.getUser();
        this.userRole = this.userRoleService.GetUserRole(this.User);
        await this.loadDetails();
    }

    public get isAdmin(): boolean {
        return this.userRole === 1 || this.userRole === 9 || this.userRole === 15;
    }

    public get isPending(): boolean {
        return this.details && Number(this.details.requestStatusId) === 1;
    }

    public get showPendingAdminView(): boolean {
        return this.isPending && this.isAdmin;
    }

    public get isRejected(): boolean {
        return this.details && Number(this.details.requestStatusId) === 3;
    }

    public get hasProviderComment(): boolean {
        return !!(this.details && this.details.providerComment &&
            String(this.details.providerComment).trim() !== '');
    }

    public get hasRejectionReason(): boolean {
        return !!(this.details && this.details.rejectionReason &&
            String(this.details.rejectionReason).trim() !== '');
    }

    public get rejectReasonIsValid(): boolean {
        return this.rejectReason !== null && this.rejectReason.trim() !== '';
    }

    public get comparisonFields(): any[] {
        if (this.details && this.details.changedFields && this.details.changedFields.length > 0) {
            return this.details.changedFields;
        }
        return this.buildFallbackChangedFields();
    }

    public get showReferenceNumber(): boolean {
        const customerType = Number(this.details.customerType);
        return customerType !== 0 && customerType !== 3;
    }

    public get showEnterpriseUnifiedNumber(): boolean {
        const customerType = Number(this.details.customerType);
        return customerType === 2 || customerType === 3;
    }

    public get referenceLabel(): string {
        switch (Number(this.details.customerType)) {
            case 1:
                return this.$t('shared.budgetNumber').toString();
            case 2:
                return this.$t('shared.licenseNumber').toString();
            case 3:
                return this.$t('shared.enterpriseUnifiedNumber').toString();
            case 4:
                return this.$t('shared.internationalCommercialRegistrationNumber').toString();
            default:
                return this.$t('shared.enterpriseUnifiedNumber').toString();
        }
    }

    public getSenderTypeText(type: number) {
        const types: string[] = [];
        if ((type & 1) === 1) { types.push(this.$t('shared.advertisement').toString()); }
        if ((type & 2) === 2) { types.push(this.$t('shared.service').toString()); }
        if ((type & 4) === 4) { types.push(this.$t('shared.awareness').toString()); }
        if ((type & 8) === 8) { types.push(this.$t('shared.warning').toString()); }
        return types.length ? types.join(', ') : '-';
    }

    public async loadDetails() {
        const id = this.$route.params.id;
        const response = await this.service.GetDetails(id);
        if (!response || !response.isSuccess) {
            Toaster.error((response && response.errorMessage) || this.$t('shared.errorOccurred').toString());
            this.back();
            return;
        }
        const result = response.result || {};
        const currentLetterName =
            result.currentDelegationLetterFileName || result.CurrentDelegationLetterFileName || '';
        const requestedLetterName =
            result.requestedDelegationLetterFileName || result.RequestedDelegationLetterFileName || '';
        const currentLetterId =
            result.currentDelegationLetterAttachmentId || result.CurrentDelegationLetterAttachmentId || '';
        const requestedLetterId =
            result.requestedDelegationLetterAttachmentId || result.RequestedDelegationLetterAttachmentId || '';
        this.details = {
            id: result.id || result.Id || '',
            displayId: result.displayId || result.DisplayId || '',
            clientName: result.clientName || result.ClientName || '',
            senderName: result.senderName || result.SenderName || '',
            senderType: result.senderType != null ? result.senderType : (result.SenderType || 0),
            customerType: result.customerType != null
                ? Number(result.customerType)
                : (result.CustomerType != null ? Number(result.CustomerType) : 0),
            crNumber: result.crNumber || result.CrNumber || '',
            enterpriseUnifiedNumber:
                result.enterpriseUnifiedNumber || result.EnterpriseUnifiedNumber || '',
            senderExpiryDate: result.senderExpiryDate || result.SenderExpiryDate || '',
            requestStatus: result.requestStatus || result.RequestStatus || '',
            requestStatusId: this.resolveRequestStatusId(result),
            createdDate: result.createdDate || result.CreatedDate || '',
            providerComment: result.providerComment || result.ProviderComment || '',
            adminComment: result.adminComment || result.AdminComment || '',
            rejectionReason: result.rejectionReason || result.RejectionReason || '',
            updateTypeName: result.updateTypeName || result.UpdateTypeName || '',
            currentRepresentativeName:
                result.currentRepresentativeName || result.CurrentRepresentativeName || '',
            currentRepresentativeEmail:
                result.currentRepresentativeEmail || result.CurrentRepresentativeEmail || '',
            currentRepresentativeMobile:
                result.currentRepresentativeMobile || result.CurrentRepresentativeMobile || '',
            requestedRepresentativeName:
                result.requestedRepresentativeName || result.RequestedRepresentativeName || '',
            requestedRepresentativeEmail:
                result.requestedRepresentativeEmail || result.RequestedRepresentativeEmail || '',
            requestedRepresentativeMobile:
                result.requestedRepresentativeMobile || result.RequestedRepresentativeMobile || '',
            delegationLetterChanged: !!(result.delegationLetterChanged || result.DelegationLetterChanged),
            currentDelegationLetterFileName: currentLetterName,
            currentDelegationLetterAttachmentId: currentLetterId,
            requestedDelegationLetterFileName: requestedLetterName,
            requestedDelegationLetterAttachmentId: requestedLetterId,
            changedFields: this.normalizeChangedFields(result.changedFields || result.ChangedFields),
        };
        if ((!this.details.changedFields || this.details.changedFields.length === 0) &&
            Number(this.details.requestStatusId) === 1) {
            this.details.changedFields = this.buildFallbackChangedFields();
        }
    }

    public back() {
        this.$router.push({ name: 'UpdateRequests' });
    }

    public showApproveDialog() {
        this.approveDialog = true;
    }

    public hideApproveDialog() {
        this.approveDialog = false;
    }

    public async confirmApprove() {
        const response = await this.service.Approve([this.details.id], '');
        if (!response || !response.isSuccess) {
            Toaster.error((response && response.errorMessage) || this.$t('shared.errorOccurred').toString());
            return;
        }
        Toaster.success(this.$t('dashboardPage.RequestApprovedsuccessfully').toString());
        this.hideApproveDialog();
        this.$router.push({ name: 'UpdateRequests' });
    }

    public showRejectDialog() {
        this.rejectReason = '';
        this.rejectDialog = true;
    }

    public hideRejectDialog() {
        this.rejectDialog = false;
        this.rejectReason = '';
    }

    public async confirmReject() {
        if (!this.rejectReasonIsValid) {
            return;
        }
        const response = await this.service.Reject([this.details.id], this.rejectReason.trim());
        if (!response || !response.isSuccess) {
            Toaster.error((response && response.errorMessage) || this.$t('shared.errorOccurred').toString());
            return;
        }
        Toaster.success(this.$t('dashboardPage.RequestRejectedsuccessfully').toString());
        this.hideRejectDialog();
        this.$router.push({ name: 'UpdateRequests' });
    }

    public downloadUpdateRequestAttachment(attachmentId: string) {
        if (!attachmentId) {
            return;
        }
        window.open(window.$config.HostName + 'api/update-requests/attachments/' + attachmentId, '_blank');
    }

    public downloadSenderRequestAttachment(attachmentId: string) {
        if (!attachmentId) {
            return;
        }
        window.open(window.$config.HostName + 'api/sender-requests/attachments/' + attachmentId, '_blank');
    }

    private resolveRequestStatusId(result: any): number {
        const raw = result.requestStatusId != null
            ? result.requestStatusId
            : result.RequestStatusId;
        const parsed = Number(raw);
        if (!isNaN(parsed) && parsed > 0) {
            return parsed;
        }
        const statusText = String(result.requestStatus || result.RequestStatus || '').toLowerCase();
        if (statusText.indexOf('pending') >= 0) {
            return 1;
        }
        if (statusText.indexOf('approved') >= 0) {
            return 2;
        }
        if (statusText.indexOf('rejected') >= 0) {
            return 3;
        }
        return 0;
    }

    private normalizeChangedFields(fields: any): any[] {
        if (!fields || !Array.isArray(fields)) {
            return [];
        }
        return fields.map((f: any) => {
            return {
                fieldName: f.fieldName || f.FieldName || '',
                currentValue: f.currentValue || f.CurrentValue || '',
                requestedValue: f.requestedValue || f.RequestedValue || '',
                isAttachment: !!(f.isAttachment || f.IsAttachment),
                currentAttachmentId: f.currentAttachmentId || f.CurrentAttachmentId || '',
                requestedAttachmentId: f.requestedAttachmentId || f.RequestedAttachmentId || '',
            };
        });
    }

    private buildFallbackChangedFields(): any[] {
        const fields: any[] = [];
        fields.push({
            fieldName: this.$t('shared.representativeName').toString(),
            currentValue: this.details.currentRepresentativeName || '-',
            requestedValue: this.details.requestedRepresentativeName || '-',
            isAttachment: false,
            currentAttachmentId: '',
            requestedAttachmentId: '',
        });
        fields.push({
            fieldName: this.$t('shared.representativeEmail').toString(),
            currentValue: this.details.currentRepresentativeEmail || '-',
            requestedValue: this.details.requestedRepresentativeEmail || '-',
            isAttachment: false,
            currentAttachmentId: '',
            requestedAttachmentId: '',
        });
        fields.push({
            fieldName: this.$t('shared.representativePhoneNumber').toString(),
            currentValue: this.details.currentRepresentativeMobile || '-',
            requestedValue: this.details.requestedRepresentativeMobile || '-',
            isAttachment: false,
            currentAttachmentId: '',
            requestedAttachmentId: '',
        });
        if (this.details.delegationLetterChanged ||
            this.details.requestedDelegationLetterAttachmentId ||
            this.details.currentDelegationLetterAttachmentId) {
            const letterLabel = this.$t('shared.delegationLetter').toString();
            fields.push({
                fieldName: letterLabel,
                currentValue: this.details.currentDelegationLetterFileName || letterLabel,
                requestedValue: this.details.requestedDelegationLetterFileName || letterLabel,
                isAttachment: true,
                currentAttachmentId: this.details.currentDelegationLetterAttachmentId || '',
                requestedAttachmentId: this.details.requestedDelegationLetterAttachmentId || '',
            });
        }
        return fields;
    }
}
